import { Component, effect, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { SearchOptionsDialogComponent } from '../main-dashboard/search-options-dialog/search-options-dialog.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MaterialModule } from '../../../modules/material.module';
import { WelcomeTutorialDialogComponent } from '../main-dashboard/welcome-tutorial-dialog/welcome-tutorial-dialog.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AuthService } from '../../../authentication/auth.service';
import { OverlayModule } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { AccountService } from '../../../shared/services/account.service';

@Component({
  selector: 'app-tool-bar',
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MaterialModule,
    OverlayModule
  ],
  standalone: true,
  templateUrl: './tool-bar.component.html',
  styleUrl: './tool-bar.component.scss',
  animations: [
    trigger('toggleContainer', [
      state('expand', style({
        height: '*', // Use '*' to allow the height to expand to its content
        opacity: 1,
        overflow: 'hidden'
      })),
      state('collapse', style({
        height: '0px',
        opacity: 0,
        overflow: 'hidden',
        display: 'none',
      })),
      transition('expand <=> collapse', animate('0.5s ease-in-out'))
    ])
  ],
})
export class ToolBarComponent {
  private readonly dialog = inject(MatDialog);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private authService = inject(AuthService);
  private router = inject(Router);
  protected accountService = inject(AccountService);

  readonly SHOW_ITEMS = {
    TRASH: 'TRASH',
    DISPLAY: 'DISPLAY',
    WORKLOAD: 'WORKLOAD',
  };

  showWelcomeDialog = input<boolean>(false);
  navBarSaveButton = input({
    disabled: true,
    text: 'Saved',
  });
  showTreeView = input(this.SHOW_ITEMS.TRASH);
  applySearchFilter = output<string>();
  showAccountSettings = output();
  logoutAccount = output();
  saveItem = output();
  handleAfterCloseWelcomeDialog = output();
  isSmallScreen = false;
  isToggleMenu = false;
  accountSettingTooltip = ''

  activeToggle = signal<string>(this.SHOW_ITEMS.DISPLAY);

  buttonOptions = [
    {
      type: this.SHOW_ITEMS.TRASH,
      tooltip: 'Trash',
      icon: 'delete',
    },
    {
      type: this.SHOW_ITEMS.DISPLAY,
      tooltip: 'Tree View',
      icon: 'list',
    },
    // TODO: Add workload view back
    // {
    //   type: this.SHOW_ITEMS.WORKLOAD,
    //   tooltip: 'Workload View',
    //   icon: 'clear_all',
    // },
  ];

  showSearchInputHelp = false;

  constructor() {
    effect(() => {
      if (this.showWelcomeDialog()) {
        this.showWelcomeTutorial();
      }
    });
  }

  ngOnInit() {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.TabletPortrait])
      .subscribe((result) => {
        this.isSmallScreen = result.matches;
      });
    this.accountSettingTooltip = `Account Settings 
      ${this.authService.getCurrentUser().email}`;
  }

  applyTextFilter(searchString: string) {
    this.applySearchFilter.emit(searchString);
  }

  showAccountSettingsDialog() {
    this.showAccountSettings.emit();
  }

  toggleView(viewType: string) {
    this.activeToggle.set(viewType);
    if (viewType === this.SHOW_ITEMS.DISPLAY) {
      this.router.navigate(['']);
    } else if (viewType === this.SHOW_ITEMS.TRASH) {
      this.router.navigate(['trash']);
    } else if (viewType === this.SHOW_ITEMS.WORKLOAD) {
      this.router.navigate(['workload-view']);
    }
  }

  save() {
    this.saveItem.emit();
  }

  /**
   * Display a modal dialog for search options.
   */
  showSearchOptions(): void {
    this.dialog.closeAll();
    this.dialog.open(SearchOptionsDialogComponent);
  }

  /**
   * Display a modal dialog with the intro tutorial informing user of how to use ProjectZen.
   */
  showWelcomeTutorial(): void {
    this.dialog.closeAll();
    const dialogRef = this.dialog.open(WelcomeTutorialDialogComponent);

    dialogRef.afterClosed().subscribe(() => {
      this.handleAfterCloseWelcomeDialog.emit();
    });
  }

  /**
   * Logout function for when click logout button.
   */
  logout() {
    this.logoutAccount.emit();
  }

  toggleMenu() {
    this.isToggleMenu = !this.isToggleMenu;
  }

  handleSearchInputHelp() {
    this.showSearchInputHelp = true;
    setTimeout(() => {
      this.showSearchInputHelp = false;
    }, 3000)
  }
}
